/**
 * Alcance real do servidor.
 *
 * `navigator.onLine` só sabe se existe adaptador de rede com link — não se dá
 * para falar com alguém. No Windows ele diz "conectado" com Wi-Fi de fazenda
 * que autentica mas não navega, com VPN caída, com cabo sem rota. Foi
 * exatamente isso: no notebook a faixa de offline não apareceu porque o
 * sistema afirmava estar online.
 *
 * Comprovado no app: com offline forçado de verdade, `navigator.onLine` vira
 * false e a faixa aparece. A lógica estava certa; a fonte de verdade é que
 * mentia.
 *
 * Aqui a pergunta passa a ser respondida batendo no servidor. `navigator.onLine`
 * continua útil como atalho: quando ele diz "desconectado", está certo — e aí
 * nem vale gastar uma requisição.
 */

const URL_BASE = process.env.REACT_APP_SUPABASE_URL;
const CHAVE_ANONIMA = process.env.REACT_APP_SUPABASE_ANON_KEY;
const TEMPO_LIMITE_MS = 6000;

// Estado conhecido. `isOnline()` continua síncrono para não obrigar todo
// chamador a virar assíncrono; quem atualiza é o monitor, em segundo plano.
let alcancavel = true;
let verificandoAgora = null;
const ouvintes = new Set();

const avisar = () => ouvintes.forEach((fn) => { try { fn(estaAlcancavel()); } catch { /* ignora */ } });

/** `navigator.onLine === false` é confiável: se não há link, não há rota. */
const semLinkDeRede = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

export const estaAlcancavel = () => !semLinkDeRede() && alcancavel;

/**
 * Bate no servidor para saber se ele responde.
 *
 * Usa o endpoint de saúde do Auth: é barato — não gasta cota de banco nem
 * depende de sessão válida. `no-store` para o navegador não responder de cache
 * e mascarar a queda.
 *
 * O `apikey` NÃO é opcional, embora a rota não exija sessão. O gateway do
 * Supabase barra qualquer requisição sem chave antes de chegar no GoTrue, e
 * responde 401. Sem este cabeçalho a verificação falhava sempre, e o app se
 * declarava fora de alcance mesmo com internet perfeita — enfileirando escrita
 * que poderia ter ido direto. A chave anônima é pública, já vai no pacote.
 */
export async function verificarAlcance() {
  if (semLinkDeRede()) {
    if (alcancavel) { alcancavel = false; avisar(); }
    return false;
  }
  // Uma verificação por vez: várias telas podem pedir ao mesmo tempo.
  if (verificandoAgora) return verificandoAgora;

  verificandoAgora = (async () => {
    let resultado = false;
    // AbortController em vez de `AbortSignal.timeout`: aquele não existe em
    // todo ambiente, e quando falta a chamada estoura DENTRO do try — o que
    // faria toda verificação responder "inalcançável" sem nem tentar.
    const controle = new AbortController();
    const corta = setTimeout(() => controle.abort(), TEMPO_LIMITE_MS);
    try {
      const resp = await fetch(`${URL_BASE}/auth/v1/health`, {
        method: 'GET',
        cache: 'no-store',
        headers: { apikey: CHAVE_ANONIMA },
        signal: controle.signal,
      });
      resultado = !!resp?.ok;
    } catch {
      resultado = false;
    } finally {
      clearTimeout(corta);
    }
    if (resultado !== alcancavel) { alcancavel = resultado; avisar(); }
    verificandoAgora = null;
    return resultado;
  })();

  return verificandoAgora;
}

export const aoMudarAlcance = (fn) => {
  ouvintes.add(fn);
  return () => ouvintes.delete(fn);
};

/** Marca queda sem esperar a próxima verificação, quando uma escrita falha por rede. */
export const marcarInalcancavel = () => {
  if (alcancavel) { alcancavel = false; avisar(); }
};
